/**
 * The typed `route.meta` shape, menu groups and the per-area route arrays. Side
 * nav menus are built from these arrays by `buildNavMenu()`. Route names are in
 * route_names.ts; router assembly (layout parents, guards) is in route_setup.ts.
 */
import {OrgRole} from '@shared/auth_types';
import type {Component} from 'vue';
import type {RouteRecordRaw} from 'vue-router';
import {RouteNames} from './route_names';

// ---------------------------------------------------------------------------
// Route metadata model
// ---------------------------------------------------------------------------

declare module 'vue-router' {
   interface RouteMeta {
      /** Label shown in side nav menus. Entries without it never render in a menu. */
      menuName?: string;

      /** Document/app-header title; defaults to menuName when not set. */
      pageTitle?: string;

      /** Font Awesome icon string ('fas fa-house'); must be registered in fontawesome_setup. */
      faIcon?: string;

      /** Key into the owning area's menu-group registry (e.g. 'account', 'organization'). */
      menuGroup?: string;

      /** Hide from nav menus (detail/param routes like /boards/:boardId). */
      hideFromNav?: boolean;

      /** Render a divider above this entry in the menu. */
      dividerBefore?: boolean;

      /** Route requires authentication (enforced by the router guard). */
      requiresAuth?: boolean;

      /** Route requires the system-wide admin user role. The guard shows the
       * not-found page to everyone else. */
      requiresAdmin?: boolean;

      /** Organization roles allowed to see this route. Not enforced yet. */
      allowedRoles?: readonly OrgRole[];

      /** Custom menu-item component (badges, live counts). Wrap in markRaw(). */
      menuComp?: Component;
   }
}

/** A side nav menu group for one area. */
export interface IMenuGroup {
   id: string;
   label: string;
   faIcon?: string;

   /** Organization roles allowed to see the group. Not enforced yet. */
   allowedRoles?: readonly OrgRole[];
}

// ---------------------------------------------------------------------------
// Lazy page imports
// ---------------------------------------------------------------------------

const NotFound = () => import('@c/NotFound.vue');
const BoardPage = () => import('@c/board/BoardPage.vue');
const BoardSettingsWorkspace = () => import('@c/board/board-settings/BoardSettingsWorkspace.vue');

const SignInPage = () => import('@c/auth/SignInPage.vue');
const SignUpPage = () => import('@c/auth/SignUpPage.vue');
const ForgotPasswordPage = () => import('@c/auth/ForgotPasswordPage.vue');
const ResetPasswordPage = () => import('@c/auth/ResetPasswordPage.vue');
const VerifyEmailPage = () => import('@c/auth/VerifyEmailPage.vue');
const InvitationAcceptPage = () => import('@c/auth/InvitationAcceptPage.vue');

const UserProfilePage = () => import('@c/settings/UserProfilePage.vue');
const AccountSecurityPage = () => import('@c/settings/AccountSecurityPage.vue');
const ApiKeysPage = () => import('@c/settings/ApiKeysPage.vue');
const MyInvitationsPage = () => import('@c/settings/MyInvitationsPage.vue');
const OrgGeneralPage = () => import('@c/settings/OrgGeneralPage.vue');
const OrgMembersPage = () => import('@c/settings/OrgMembersPage.vue');
const OrgApiKeysPage = () => import('@c/settings/OrgApiKeysPage.vue');
const SettingsPlaceholderPage = () => import('@c/settings/SettingsPlaceholderPage.vue');
const TestingCommPage = () => import('@c/settings/TestingCommPage.vue');

const AdminUsersPage = () => import('@c/admin/AdminUsersPage.vue');
const AdminUserDetailPage = () => import('@c/admin/AdminUserDetailPage.vue');

const DesignTokensPage = () => import('@src/design_language/pages/DesignTokensPage.vue');
const CommonComponentsPage = () => import('@c/dev/CommonComponentsPage.vue');
const IconReferencePage = () => import('@c/dev/IconReferencePage.vue');
const RawStoreDataPage = () => import('@c/dev/RawStoreDataPage.vue');
const LiveFormPlayground = () => import('@c/dev/LiveFormPlayground.vue');

// ---------------------------------------------------------------------------
// Base routes (outside the area layouts: root redirect, catch-all)
// ---------------------------------------------------------------------------

export const BASE_ROUTES: RouteRecordRaw[] = [
   {
      path: '/',
      redirect: {name: RouteNames.BOARD},
   },
   {
      path: '/:pathMatch(.*)*',
      name: RouteNames.NOT_FOUND,
      component: NotFound,
      meta: {pageTitle: 'Not Found'},
   },
];

// ---------------------------------------------------------------------------
// Auth area (children of AuthLayout at /auth). Public: reachable while signed
// out. None of these appear in nav menus.
// ---------------------------------------------------------------------------

export const AUTH_ROUTES: RouteRecordRaw[] = [
   {
      path: '',
      redirect: {name: RouteNames.AUTH_SIGN_IN},
   },
   {
      path: 'sign-in',
      name: RouteNames.AUTH_SIGN_IN,
      component: SignInPage,
      meta: {pageTitle: 'Sign in'},
   },
   {
      path: 'sign-up',
      name: RouteNames.AUTH_SIGN_UP,
      component: SignUpPage,
      meta: {pageTitle: 'Create account'},
   },
   {
      path: 'forgot-password',
      name: RouteNames.AUTH_FORGOT_PASSWORD,
      component: ForgotPasswordPage,
      meta: {pageTitle: 'Forgot password'},
   },
   {
      // Arrives with ?token= from the emailed link
      path: 'reset-password',
      name: RouteNames.AUTH_RESET_PASSWORD,
      component: ResetPasswordPage,
      meta: {pageTitle: 'Reset password'},
   },
   {
      path: 'verify-email',
      name: RouteNames.AUTH_VERIFY_EMAIL,
      component: VerifyEmailPage,
      meta: {pageTitle: 'Verify email'},
   },
   {
      // Landing page from an organization invitation email
      path: 'invitation/:invitationId',
      name: RouteNames.AUTH_INVITATION,
      component: InvitationAcceptPage,
      meta: {pageTitle: 'Invitation'},
   },
];

// ---------------------------------------------------------------------------
// Main area (children of MainLayout at /boards). The main area's side nav is
// the store-driven board tree, so these routes are not menu entries.
// ---------------------------------------------------------------------------

export const MAIN_ROUTES: RouteRecordRaw[] = [
   {
      // Old fields-editor URL; the editor now lives in the board-settings workspace.
      path: ':boardId/fields/:fieldId?',
      redirect: (to) => ({
         name: RouteNames.BOARD_SETTINGS_FIELDS,
         params: {
            boardId: to.params.boardId,
            fieldId: to.params.fieldId === '' ? undefined : to.params.fieldId,
         },
      }),
   },
   {
      path: ':boardId?',
      name: RouteNames.BOARD,
      component: BoardPage,
      meta: {
         pageTitle: 'Boards',
         requiresAuth: true,
         hideFromNav: true,
      },
      // The settings workspace renders in BoardPage's router-view, so the board
      // stays mounted underneath the overlay.
      children: [
         {
            path: 'settings/fields/:fieldId?',
            name: RouteNames.BOARD_SETTINGS_FIELDS,
            component: BoardSettingsWorkspace,
            meta: {
               pageTitle: 'Board settings',
               requiresAuth: true,
               hideFromNav: true,
            },
         },
         {
            path: 'settings/:section?',
            name: RouteNames.BOARD_SETTINGS,
            component: BoardSettingsWorkspace,
            meta: {
               pageTitle: 'Board settings',
               requiresAuth: true,
               hideFromNav: true,
            },
         },
      ],
   },
];

// ---------------------------------------------------------------------------
// Settings area (children of SettingsLayout at /settings). `allowedRoles` on
// the Organization group is not enforced yet.
// ---------------------------------------------------------------------------

export const SETTINGS_MENU_GROUPS: readonly IMenuGroup[] = [
   {id: 'account', label: 'Account'},
   {
      id: 'organization',
      label: 'Organization',
      allowedRoles: [OrgRole.OWNER, OrgRole.ADMIN],
   },
   {
      id: 'system',
      label: 'System',
      allowedRoles: [OrgRole.OWNER, OrgRole.ADMIN],
   },
];

export const SETTINGS_ROUTES: RouteRecordRaw[] = [
   {
      path: '',
      redirect: {name: RouteNames.SETTINGS_PROFILE},
   },
   {
      path: 'profile',
      name: RouteNames.SETTINGS_PROFILE,
      component: UserProfilePage,
      meta: {
         menuName: 'Profile',
         pageTitle: 'Profile Settings',
         faIcon: 'fas fa-user',
         menuGroup: 'account',
         requiresAuth: true,
      },
   },
   {
      path: 'account-security',
      name: RouteNames.SETTINGS_ACCOUNT_SECURITY,
      component: AccountSecurityPage,
      meta: {
         menuName: 'Security',
         pageTitle: 'Account Security',
         faIcon: 'fas fa-lock',
         menuGroup: 'account',
         requiresAuth: true,
      },
   },
   {
      path: 'api-keys',
      name: RouteNames.SETTINGS_API_KEYS,
      component: ApiKeysPage,
      meta: {
         menuName: 'API keys',
         pageTitle: 'API Keys',
         faIcon: 'fas fa-key',
         menuGroup: 'account',
         requiresAuth: true,
      },
   },
   {
      path: 'invitations',
      name: RouteNames.SETTINGS_INVITATIONS,
      component: MyInvitationsPage,
      meta: {
         menuName: 'Invitations',
         pageTitle: 'My Invitations',
         faIcon: 'fas fa-envelope',
         menuGroup: 'account',
         requiresAuth: true,
      },
   },
   {
      path: 'organization',
      name: RouteNames.SETTINGS_ORG_GENERAL,
      component: OrgGeneralPage,
      meta: {
         menuName: 'General',
         pageTitle: 'Organization Settings',
         faIcon: 'fas fa-sliders',
         menuGroup: 'organization',
         requiresAuth: true,
         allowedRoles: [OrgRole.OWNER, OrgRole.ADMIN],
      },
   },
   {
      path: 'members',
      name: RouteNames.SETTINGS_ORG_MEMBERS,
      component: OrgMembersPage,
      meta: {
         menuName: 'Members',
         pageTitle: 'Organization Members',
         faIcon: 'fas fa-users',
         menuGroup: 'organization',
         requiresAuth: true,
         allowedRoles: [OrgRole.OWNER, OrgRole.ADMIN],
      },
   },
   {
      path: 'org-api-keys',
      name: RouteNames.SETTINGS_ORG_API_KEYS,
      component: OrgApiKeysPage,
      meta: {
         menuName: 'API keys',
         pageTitle: 'Organization API Keys',
         faIcon: 'fas fa-key',
         menuGroup: 'organization',
         requiresAuth: true,
         allowedRoles: [OrgRole.OWNER, OrgRole.ADMIN],
      },
   },
   {
      path: 'security',
      name: RouteNames.SETTINGS_ORG_SECURITY,
      component: SettingsPlaceholderPage,
      meta: {
         menuName: 'Org Security',
         pageTitle: 'Security Settings',
         faIcon: 'fas fa-shield-halved',
         menuGroup: 'organization',
         requiresAuth: true,
         allowedRoles: [OrgRole.OWNER, OrgRole.ADMIN],
      },
   },
   {
      path: 'billing',
      name: RouteNames.SETTINGS_ORG_BILLING,
      component: SettingsPlaceholderPage,
      meta: {
         menuName: 'Billing',
         pageTitle: 'Billing',
         faIcon: 'fas fa-credit-card',
         menuGroup: 'organization',
         requiresAuth: true,
         allowedRoles: [OrgRole.OWNER],
      },
   },
   {
      // Deployment smoke tests for backend communication (tRPC + websocket)
      path: 'testing-comm',
      name: RouteNames.SETTINGS_TESTING_COMM,
      component: TestingCommPage,
      meta: {
         menuName: 'Comm testing',
         pageTitle: 'Communication Testing',
         faIcon: 'fas fa-satellite-dish',
         menuGroup: 'system',
         requiresAuth: true,
         allowedRoles: [OrgRole.OWNER, OrgRole.ADMIN],
      },
   },
];

// ---------------------------------------------------------------------------
// Admin area (children of AdminLayout at /admin). The router guard shows the
// not-found page to anyone without the system admin role.
// ---------------------------------------------------------------------------

export const ADMIN_ROUTES: RouteRecordRaw[] = [
   {
      path: '',
      redirect: {name: RouteNames.ADMIN_USERS},
   },
   {
      path: 'users',
      name: RouteNames.ADMIN_USERS,
      component: AdminUsersPage,
      meta: {
         menuName: 'Users',
         pageTitle: 'Admin — Users',
         faIcon: 'fas fa-users-gear',
         requiresAuth: true,
      },
   },
   {
      path: 'users/:userId',
      name: RouteNames.ADMIN_USER_DETAIL,
      component: AdminUserDetailPage,
      meta: {
         pageTitle: 'Admin — User Detail',
         requiresAuth: true,
         hideFromNav: true,
      },
   },
];

// ---------------------------------------------------------------------------
// Dev area (children of DevLayout at /dev). Open in dev builds; in production
// it requires the app-admin role and masks as NotFound (route_setup.ts).
// ---------------------------------------------------------------------------

/** Nav groups for the dev-area drawer; both render as collapsible folders. */
export const DEV_MENU_GROUPS: readonly IMenuGroup[] = [
   {id: 'design-language', label: 'Design Language', faIcon: 'fas fa-palette'},
   {id: 'core-patterns', label: 'Core patterns', faIcon: 'fas fa-shapes'},
   {id: 'product-patterns', label: 'Product patterns', faIcon: 'fas fa-table-columns'},
];

const DesignLanguagePage = () => import('@src/design_language/pages/GuidelinesPage.vue');
const PosthogTestsPage = () => import('@c/dev/PosthogTestsPage.vue');
const PatternSurfaces = () => import('@src/design_language/patterns/PatternSurfaces.vue');
const PatternAppBar = () => import('@src/design_language/patterns/PatternAppBar.vue');
const PatternBoardToolbar = () => import('@src/design_language/patterns/PatternBoardToolbar.vue');
const PatternBoardSurface = () => import('@src/design_language/patterns/PatternBoardSurface.vue');
const PatternCardFront = () => import('@src/design_language/patterns/PatternCardFront.vue');
const PatternCardDetail = () => import('@src/design_language/patterns/PatternCardDetail.vue');
const PatternCardBack = () => import('@src/design_language/patterns/PatternCardBack.vue');
const PatternComments = () => import('@src/design_language/patterns/PatternComments.vue');
const PatternSubItems = () => import('@src/design_language/patterns/PatternSubItems.vue');
const PatternNotifications = () => import('@src/design_language/patterns/PatternNotifications.vue');
const PatternGlobalSearch = () => import('@src/design_language/patterns/PatternGlobalSearch.vue');
const PatternButtons = () => import('@src/design_language/patterns/PatternButtons.vue');
const PatternChips = () => import('@src/design_language/patterns/PatternChips.vue');
const PatternMenus = () => import('@src/design_language/patterns/PatternMenus.vue');
const PatternFiltering = () => import('@src/design_language/patterns/PatternFiltering.vue');
const PatternFilterTable = () => import('@src/design_language/patterns/PatternFilterTable.vue');
const PatternDenseForm = () => import('@src/design_language/patterns/PatternDenseForm.vue');
const PatternLiveEdit = () => import('@src/design_language/patterns/PatternLiveEdit.vue');
const PatternDialogs = () => import('@src/design_language/patterns/PatternDialogs.vue');
const PatternInlineEdit = () => import('@src/design_language/patterns/PatternInlineEdit.vue');
const PatternUndo = () => import('@src/design_language/patterns/PatternUndo.vue');
const PatternConfigShells = () => import('@src/design_language/patterns/PatternConfigShells.vue');
const PatternPageSkeleton = () => import('@src/design_language/patterns/PatternPageSkeleton.vue');
const PatternPanelAnatomy = () => import('@src/design_language/patterns/PatternPanelAnatomy.vue');
const PatternEmptyStates = () => import('@src/design_language/patterns/PatternEmptyStates.vue');
const PatternErrorStates = () => import('@src/design_language/patterns/PatternErrorStates.vue');
const PatternDoDont = () => import('@src/design_language/patterns/PatternDoDont.vue');

export const DEV_ROUTES: RouteRecordRaw[] = [
   {
      path: '',
      redirect: {name: RouteNames.DEV_DESIGN_LANGUAGE},
   },
   {
      path: 'design-language',
      name: RouteNames.DEV_DESIGN_LANGUAGE,
      component: DesignLanguagePage,
      meta: {
         menuName: 'Design language',
         pageTitle: 'Design Language',
         menuGroup: 'design-language',
      },
   },
   {
      path: 'design-system',
      name: RouteNames.DEV_DESIGN_SYSTEM,
      component: DesignTokensPage,
      meta: {
         menuName: 'Tokens',
         pageTitle: 'Design Tokens',
         menuGroup: 'design-language',
      },
   },
   {
      path: 'icons',
      name: RouteNames.DEV_ICONS,
      component: IconReferencePage,
      meta: {
         menuName: 'Icons',
         pageTitle: 'Icon Reference',
         menuGroup: 'design-language',
      },
   },
   {
      path: 'posthog-tests',
      name: RouteNames.DEV_POSTHOG_TESTS,
      component: PosthogTestsPage,
      meta: {
         menuName: 'PostHog tests',
         pageTitle: 'PostHog Tests',
      },
   },
   {
      path: 'components',
      name: RouteNames.DEV_COMPONENTS,
      component: CommonComponentsPage,
      meta: {
         menuName: 'Common components',
         pageTitle: 'Common Components',
      },
   },
   {
      path: 'raw-store',
      name: RouteNames.DEV_RAW_STORE,
      component: RawStoreDataPage,
      meta: {
         menuName: 'Raw store',
         pageTitle: 'Raw Store Data',
      },
   },
   {
      path: 'live-form',
      name: RouteNames.DEV_LIVE_FORM,
      component: LiveFormPlayground,
      meta: {
         menuName: 'Live form',
         pageTitle: 'Live Form Playground',
      },
   },

   // Pattern pages: each is the live example plus that surface's rules.
   // Core patterns run fundamentals-first; product patterns are the board-and-app surfaces built from them.
   {
      path: 'pattern/surfaces',
      name: RouteNames.DEV_PAT_SURFACES,
      component: PatternSurfaces,
      meta: {menuName: 'Surface architecture', pageTitle: 'Pattern: Surface Architecture', menuGroup: 'core-patterns'},
   },
   {
      path: 'pattern/config-shells',
      name: RouteNames.DEV_PAT_CONFIG_SHELLS,
      component: PatternConfigShells,
      meta: {menuName: 'Config shells', pageTitle: 'Pattern: Config Shells', menuGroup: 'core-patterns'},
   },
   {
      path: 'pattern/page-skeleton',
      name: RouteNames.DEV_PAT_PAGE_SKELETON,
      component: PatternPageSkeleton,
      meta: {menuName: 'Page skeleton', pageTitle: 'Pattern: Page Skeleton', menuGroup: 'core-patterns'},
   },
   {
      path: 'pattern/panel-anatomy',
      name: RouteNames.DEV_PAT_PANEL_ANATOMY,
      component: PatternPanelAnatomy,
      meta: {menuName: 'Panel anatomy', pageTitle: 'Pattern: Panel Anatomy', menuGroup: 'core-patterns'},
   },
   {
      path: 'pattern/buttons',
      name: RouteNames.DEV_PAT_BUTTONS,
      component: PatternButtons,
      meta: {menuName: 'Buttons & controls', pageTitle: 'Pattern: Buttons & Controls', menuGroup: 'core-patterns'},
   },
   {
      path: 'pattern/chips',
      name: RouteNames.DEV_PAT_CHIPS,
      component: PatternChips,
      meta: {menuName: 'Chips & labels', pageTitle: 'Pattern: Chips & Labels', menuGroup: 'core-patterns'},
   },
   {
      path: 'pattern/menus',
      name: RouteNames.DEV_PAT_MENUS,
      component: PatternMenus,
      meta: {menuName: 'Menus & popovers', pageTitle: 'Pattern: Menus & Popovers', menuGroup: 'core-patterns'},
   },
   {
      path: 'pattern/dialogs',
      name: RouteNames.DEV_PAT_DIALOGS,
      component: PatternDialogs,
      meta: {menuName: 'Dialogs', pageTitle: 'Pattern: Dialog Anatomy', menuGroup: 'core-patterns'},
   },
   {
      path: 'pattern/inline-edit',
      name: RouteNames.DEV_PAT_INLINE_EDIT,
      component: PatternInlineEdit,
      meta: {menuName: 'Inline edit & composers', pageTitle: 'Pattern: Inline Edit & Composers', menuGroup: 'core-patterns'},
   },
   {
      path: 'pattern/live-edit',
      name: RouteNames.DEV_PAT_LIVE_EDIT,
      component: PatternLiveEdit,
      meta: {menuName: 'Live edit & feedback', pageTitle: 'Pattern: Live Edit & Feedback', menuGroup: 'core-patterns'},
   },
   {
      path: 'pattern/undo',
      name: RouteNames.DEV_PAT_UNDO,
      component: PatternUndo,
      meta: {menuName: 'Destructive & undo', pageTitle: 'Pattern: Destructive Actions & Undo', menuGroup: 'core-patterns'},
   },
   {
      path: 'pattern/dense-form',
      name: RouteNames.DEV_PAT_DENSE_FORM,
      component: PatternDenseForm,
      meta: {menuName: 'Dense form', pageTitle: 'Pattern: Dense Form', menuGroup: 'core-patterns'},
   },
   {
      path: 'pattern/filtering',
      name: RouteNames.DEV_PAT_FILTERING,
      component: PatternFiltering,
      meta: {menuName: 'Filtering & search', pageTitle: 'Pattern: Filtering & Search', menuGroup: 'core-patterns'},
   },
   {
      path: 'pattern/filter-table',
      name: RouteNames.DEV_PAT_FILTER_TABLE,
      component: PatternFilterTable,
      meta: {menuName: 'Filterable table', pageTitle: 'Pattern: Filterable Table', menuGroup: 'core-patterns'},
   },
   {
      path: 'pattern/empty-states',
      name: RouteNames.DEV_PAT_EMPTY_STATES,
      component: PatternEmptyStates,
      meta: {menuName: 'Empty states', pageTitle: 'Pattern: Empty States', menuGroup: 'core-patterns'},
   },
   {
      path: 'pattern/error-states',
      name: RouteNames.DEV_PAT_ERROR_STATES,
      component: PatternErrorStates,
      meta: {menuName: 'Error & loading states', pageTitle: 'Pattern: Error & Loading States', menuGroup: 'core-patterns'},
   },
   {
      path: 'pattern/do-dont',
      name: RouteNames.DEV_PAT_DO_DONT,
      component: PatternDoDont,
      meta: {menuName: "Do / don't", pageTitle: "Pattern: Do / Don't", menuGroup: 'core-patterns'},
   },
   {
      path: 'pattern/app-bar',
      name: RouteNames.DEV_PAT_APP_BAR,
      component: PatternAppBar,
      meta: {menuName: 'App bar', pageTitle: 'Pattern: App Bar', menuGroup: 'product-patterns'},
   },
   {
      path: 'pattern/board-toolbar',
      name: RouteNames.DEV_PAT_BOARD_TOOLBAR,
      component: PatternBoardToolbar,
      meta: {menuName: 'Board chrome', pageTitle: 'Pattern: Board Chrome', menuGroup: 'product-patterns'},
   },
   {
      path: 'pattern/board-surface',
      name: RouteNames.DEV_PAT_BOARD_SURFACE,
      component: PatternBoardSurface,
      meta: {menuName: 'Board surface', pageTitle: 'Pattern: Board Surface', menuGroup: 'product-patterns'},
   },
   {
      path: 'pattern/card-front',
      name: RouteNames.DEV_PAT_CARD_FRONT,
      component: PatternCardFront,
      meta: {menuName: 'Card front', pageTitle: 'Pattern: Card Front', menuGroup: 'product-patterns'},
   },
   {
      path: 'pattern/card-detail',
      name: RouteNames.DEV_PAT_CARD_DETAIL,
      component: PatternCardDetail,
      meta: {menuName: 'Card detail container', pageTitle: 'Pattern: Card Detail Container', menuGroup: 'product-patterns'},
   },
   {
      path: 'pattern/card-back',
      name: RouteNames.DEV_PAT_CARD_BACK,
      component: PatternCardBack,
      meta: {menuName: 'Card back layout', pageTitle: 'Pattern: Card Back Layout', menuGroup: 'product-patterns'},
   },
   {
      path: 'pattern/comments',
      name: RouteNames.DEV_PAT_COMMENTS,
      component: PatternComments,
      meta: {menuName: 'Comments', pageTitle: 'Pattern: Comments', menuGroup: 'product-patterns'},
   },
   {
      path: 'pattern/sub-items',
      name: RouteNames.DEV_PAT_SUB_ITEMS,
      component: PatternSubItems,
      meta: {menuName: 'Sub-items', pageTitle: 'Pattern: Sub-Items', menuGroup: 'product-patterns'},
   },
   {
      path: 'pattern/notifications',
      name: RouteNames.DEV_PAT_NOTIFICATIONS,
      component: PatternNotifications,
      meta: {menuName: 'Notifications', pageTitle: 'Pattern: Notifications', menuGroup: 'product-patterns'},
   },
   {
      path: 'pattern/global-search',
      name: RouteNames.DEV_PAT_GLOBAL_SEARCH,
      component: PatternGlobalSearch,
      meta: {menuName: 'Global search', pageTitle: 'Pattern: Global Search', menuGroup: 'product-patterns'},
   },
];
